import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { SearchInput } from '../components/ui/SearchInput';
import { CountBand } from '../components/ui/CountBand';
import { SensitiveCredential } from '../components/ui/SensitiveCredential';
import { apiList, apiPost } from '../api/apiClient';
import { formatDateTime } from '../utils/formatters';
import { Smartphone, CheckCircle, Clock, TrendingUp, Award, Download, Send, RefreshCw } from 'lucide-react';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { activationTimestamp, summarizeActivations } from '../utils/mobileActivations';
import { canonicalEntityId, entityMatchesId } from '../utils/entityIdentity';


interface PageNavProps {
  safePage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  filteredLength: number;
  setPage: (fn: (p: number) => number) => void;
}

const PageNav: React.FC<PageNavProps> = ({ safePage, totalPages, startIndex, endIndex, filteredLength, setPage }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-500">
        Showing {filteredLength === 0 ? 0 : startIndex + 1}–{endIndex} of {filteredLength}
      </span>
    </div>
    <div className="flex items-center gap-1">
      <button
        onClick={() => setPage(p => Math.max(1, p - 1))}
        disabled={safePage <= 1}
        className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >Previous</button>
      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
        let pageNum: number;
        if (totalPages <= 7) pageNum = i + 1;
        else if (safePage <= 4) pageNum = i + 1;
        else if (safePage >= totalPages - 3) pageNum = totalPages - 6 + i;
        else pageNum = safePage - 3 + i;
        return (
          <button
            key={pageNum}
            onClick={() => setPage(() => pageNum)}
            className={`w-8 h-8 text-sm rounded-lg ${
              safePage === pageNum
                ? 'bg-brand-600 text-white'
                : 'hover:bg-gray-50 text-gray-700'
            }`}
          >{pageNum}</button>
        );
      })}
      <button
        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        disabled={safePage >= totalPages}
        className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >Next</button>
    </div>
  </div>
);

const exportToCSV = (data: any[]) => {
  const date = new Date().toISOString().split('T')[0];
  const filename = `intrlock-mobile-users-${date}.csv`;
  
  // CSV headers
  const headers = ['Tenant Name', 'Property', 'Unit', 'Status', 'Platform', 'Activated Date', 'Last Active'];
  
  // CSV rows
  const rows = data.map(a => [
    `"${a.tenant_name || ''}"`,
    `"${a.property_name || ''}"`,
    a.unit_number !== '—' ? `#${a.unit_number}` : '—',
    a.activated ? 'Activated' : 'Pending',
    (a.platform || '—').toUpperCase(),
    a.activated_at ? new Date(a.activated_at).toLocaleString() : '—',
    a.last_active ? new Date(a.last_active).toLocaleDateString() : '—'
  ]);
  
  // Build CSV string
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  // Create download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const MobileUsers: React.FC = () => {
  const navigate = useNavigate();
  const [activations, setActivations] = useState<any[]>([]);
  const [properties, setProperties] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [propertyFilter, setPropertyFilter] = useState<string | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'invited'>('all');
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  // Intercom App tab retired — this page only shows mobile-app activations.
  const [sendingCode, setSendingCode] = useState<Set<string | number>>(new Set());
  const [generatingCode, setGeneratingCode] = useState<Set<string | number>>(new Set());
  const [codeMessages, setCodeMessages] = useState<Record<string, { type: 'success' | 'error'; text: string }>>({});
  const [altEmailModal, setAltEmailModal] = useState<{ open: boolean; activation?: any }>({ open: false });
  const [altEmail, setAltEmail] = useState('');

  const load = useCallback(async () => {
    try {
      const [activationData, tenantData, propertyData, unitData, tenantPropertyData] = await Promise.all([
        apiList('app_activations').catch(() => []),
        apiList('tenants').catch(() => []),
        apiList('properties').catch(() => []),
        apiList('units').catch(() => []),
        apiList('tenant_properties').catch(() => []),
      ]);

      setProperties(propertyData || []);
      setTenants(tenantData || []);

      const propertyForId = (value: unknown) => (propertyData || []).find((property: any) => entityMatchesId(property, value));
      const propertyKey = (value: unknown) => {
        if (value == null || String(value).trim() === '') return '';
        const property = propertyForId(value);
        return property ? canonicalEntityId(property) : String(value);
      };
      const unitMap = new Map<string, any>();
      (unitData || []).forEach((unit: any) => {
        [unit.id, unit.uuid].filter(Boolean).forEach((key: any) => unitMap.set(String(key), unit));
      });
      const assignmentsByPerson = new Map<string, any[]>();
      (tenantPropertyData || []).forEach((assignment: any) => {
        const personId = String(assignment.person_id || assignment.tenant_id || '');
        if (!personId) return;
        const previous = assignmentsByPerson.get(personId) || [];
        assignmentsByPerson.set(personId, [...previous, assignment]);
      });
      const mobileActivations = (activationData || []).filter((a: any) => !['intercom', 'kiosk'].includes(String(a.app_type || '').toLowerCase()));
      const enriched = (tenantData || []).flatMap((tenant: any) => {
        const personId = tenant.uuid || tenant.id;
        const personAliases = [tenant.id, tenant.uuid].filter(Boolean).map(String);
        const personActivations = mobileActivations.filter((activation: any) =>
          personAliases.includes(String(activation.tenant_id || activation.person_id || '')));
        const assignments = personAliases.flatMap((alias) => assignmentsByPerson.get(alias) || []);
        const distinctAssignments = Array.from(new Map(assignments.map((assignment: any) => [
          String(assignment.id || assignment.uuid || `${assignment.property_id}:${assignment.unit_id || ''}`), assignment,
        ])).values()) as any[];
        const propertyIds = new Set<string>();
        personActivations.forEach((activation: any) => {
          const key = propertyKey(activation.property_id);
          if (key) propertyIds.add(key);
        });
        distinctAssignments.forEach((assignment: any) => {
          const key = propertyKey(assignment.property_id);
          if (key) propertyIds.add(key);
        });
        const tenantPropertyKey = propertyKey(tenant.property_id);
        if (tenantPropertyKey) propertyIds.add(tenantPropertyKey);
        if (!propertyIds.size) propertyIds.add('');

        return Array.from(propertyIds).map((propertyId) => {
          const propertyRecords = personActivations.filter((activation: any) => {
            const activationProperty = propertyKey(activation.property_id);
            return activationProperty === propertyId || (!activationProperty && propertyIds.size === 1);
          });
          const summary = summarizeActivations(propertyRecords);
          const primary = summary.primary || {};
          const assignment = distinctAssignments.find((item: any) => propertyKey(item.property_id) === propertyId) || {};
          const unitId = primary.unit_id || assignment.unit_id || (propertyIds.size === 1 ? tenant.unit_id : null);
          const unit = unitMap.get(String(unitId || '')) || {};
          const resolvedPropertyId = propertyId || propertyKey(unit.property_id);
          const property = propertyForId(resolvedPropertyId) || {};
          const codeRecord = [...propertyRecords]
            .filter((record: any) => record.activation_code || record.video_activation_code)
            .sort((left, right) => activationTimestamp(right) - activationTimestamp(left))[0] || {};
          const latestActive = [...propertyRecords]
            .filter((record: any) => record.last_active || record.last_seen_at)
            .sort((left, right) => new Date(right.last_active || right.last_seen_at).getTime() - new Date(left.last_active || left.last_seen_at).getTime())[0];
          const platforms = summary.platforms.length
            ? summary.platforms
            : Array.from(new Set(propertyRecords.map((record: any) => String(record.platform || '').toLowerCase()).filter(Boolean)));
          return {
            ...primary,
            id: `${personId}:${resolvedPropertyId || 'unassigned'}`,
            tenant_id: personId,
            property_id: resolvedPropertyId || null,
            tenant_name: [tenant.first_name, tenant.last_name].filter(Boolean).join(' ') || tenant.name || `Person #${personId}`,
            tenant_email: tenant.email || primary.email || null,
            property_name: property.name || (resolvedPropertyId ? `Property #${resolvedPropertyId}` : 'Unassigned'),
            unit_id: unitId || null,
            unit_number: unit.unit_number || assignment.unit_number || (propertyIds.size === 1 ? tenant.unit_number : null) || '—',
            activated_at: summary.activatedAt,
            last_active: latestActive?.last_active || latestActive?.last_seen_at || null,
            status: summary.status,
            activated: summary.active,
            activation_count: summary.totalCount,
            active_count: summary.activeCount,
            pending_count: summary.pendingCount,
            revoked_count: summary.revokedCount,
            platform: platforms.join(', '),
            sent_method: codeRecord.sent_via || (codeRecord.activation_code || codeRecord.video_activation_code ? 'generated' : null),
            activation_code: codeRecord.activation_code || codeRecord.video_activation_code || null,
          };
        });
      });

      setActivations(enriched);
    } catch (e) {
      console.error('Failed to load mobile users:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Send activation code
  const handleSendCode = useCallback(async (a: any) => {
    if (!a.tenant_email) {
      setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'error', text: 'No email' } }));
      return;
    }
    setSendingCode(prev => new Set(prev).add(a.id));
    setCodeMessages(prev => { const n = { ...prev }; delete n[a.id]; return n; });
    try {
      const data = await apiPost('/video/send-activation', {
        tenantId: a.tenant_id,
        email: a.tenant_email,
        tenantName: a.tenant_name || 'Tenant',
      });
      if (data.success) {
        const msg = data.emailSent ? `Sent to ${a.tenant_email}` : `Code: ${data.code}`;
        setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'success', text: msg } }));
      } else {
        setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'error', text: data.error || 'Failed' } }));
      }
    } catch {
      setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'error', text: 'Network error' } }));
    } finally {
      setSendingCode(prev => { const n = new Set(prev); n.delete(a.id); return n; });
    }
  }, []);

  const handleGenerateCode = useCallback(async (a: any, regenerate = false) => {
    if (!a.tenant_id || !a.property_id) {
      setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'error', text: 'Assign a property first' } }));
      return;
    }
    setGeneratingCode(prev => new Set(prev).add(a.id));
    setCodeMessages(prev => { const next = { ...prev }; delete next[a.id]; return next; });
    try {
      const token = localStorage.getItem('intrlock_token') || localStorage.getItem('jwt') || localStorage.getItem('token') || '';
      if (!token) throw new Error('Please sign in again to generate codes.');
      const params = new URLSearchParams({ tenantId: String(a.tenant_id), propertyId: String(a.property_id) });
      if (a.unit_id) params.set('unitId', String(a.unit_id));
      if (regenerate) params.set('regenerate', '1');
      const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
      const response = await fetch(`${apiBase}/intercom/activation-code?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.message || `Generation failed (${response.status}).`);
      const code = data.activation_code || data.code;
      if (!code) throw new Error('The server did not return an activation code.');
      setActivations(rows => rows.map(row => row.id === a.id ? { ...row, activation_code: code, activated: false, status: 'invited', sent_method: row.sent_method || 'generated', video_code_expires_at: data.expires_at } : row));
      try { await navigator.clipboard.writeText(String(code)); } catch { /* Clipboard access is optional. */ }
      setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'success', text: regenerate ? 'New code copied' : 'Code generated and copied' } }));
    } catch (error: any) {
      setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'error', text: error?.message || 'Generation failed' } }));
    } finally {
      setGeneratingCode(prev => { const next = new Set(prev); next.delete(a.id); return next; });
    }
  }, []);

  const filtered = useMemo(() => {
    let result = activations;

    if (propertyFilter !== 'all') {
      result = result.filter(a => String(a.property_id) === String(propertyFilter));
    }
    
    if (statusFilter !== 'all') result = result.filter(a => a.status === statusFilter);

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((a: any) => {
        const searchable = `${a.tenant_name} ${a.property_name} ${a.unit_number} ${a.platform || ''}`.toLowerCase();
        return searchable.includes(q);
      });
    }

    return result;
  }, [activations, search, propertyFilter, statusFilter]);

  const totalActivationRecords = activations.reduce((sum, row) => sum + Number(row.activation_count || 0), 0);
  const totalActivated = activations.reduce((sum, row) => sum + Number(row.active_count || 0), 0);
  const totalPending = activations.reduce((sum, row) => sum + Number(row.pending_count || 0), 0);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, filtered.length);
  const paginatedData = filtered.slice(startIndex, endIndex);

  useEffect(() => { setPage(1); }, [search, propertyFilter, statusFilter]);

  const columns = [
    {
      key: 'tenant_name',
      header: 'Tenant',
      render: (a: any) => (
        <div>
          <p className="font-medium text-gray-900">{a.tenant_name}</p>
        </div>
      )
    },
    {
      key: 'property_name',
      header: 'Property',
      render: (a: any) => a.property_name
    },
    {
      key: 'unit_number',
      header: 'Unit',
      render: (a: any) => a.unit_number !== '—' ? `#${a.unit_number}` : '—'
    },
    {
      key: 'platform',
      header: 'Platform',
      render: (a: any) => (
        <Badge color={a.platform === 'ios' ? 'blue' : 'green'}>
          {a.platform?.toUpperCase() || '—'}
        </Badge>
      )
    },
    {
      key: 'activation_code',
      header: 'Activation Code',
      render: (a: any) => {
        const busy = generatingCode.has(a.id);
        return a.activation_code ? <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}><SensitiveCredential value={a.activation_code}/><button type="button" disabled={busy} onClick={() => handleGenerateCode(a, true)} className="text-emerald-600 hover:text-emerald-800 text-xs underline disabled:opacity-50">{busy ? 'Generating…' : 'Regenerate'}</button></div> : <button type="button" disabled={busy || !a.property_id} onClick={(e) => { e.stopPropagation(); handleGenerateCode(a); }} className="text-xs text-brand-600 hover:text-brand-700 font-medium px-2 py-1 rounded hover:bg-brand-50 disabled:opacity-40">{busy ? 'Generating…' : 'Generate Code'}</button>;
      }
    },
    {
      key: 'activated_at',
      header: 'Activated Date',
      render: (a: any) => a.activated_at ? formatDateTime(a.activated_at) : '—'
    },
    {
      key: 'status',
      header: 'Status',
      render: (a: any) => {
        if (a.status === 'active') return <Badge status="active">Active{a.active_count > 1 ? ` · ${a.active_count} devices` : ''}</Badge>;
        if (a.status === 'invited') return <Badge status="pending">Invited{a.pending_count > 1 ? ` · ${a.pending_count}` : ''}</Badge>;
        return <Badge color="gray">Inactive</Badge>;
      }
    },
    {
      key: 'sent_method',
      header: 'Sent Via',
      render: (a: any) => {
        if (a.status === 'active') return <span className="text-green-600 text-xs">Activated {a.activated_at ? formatDateTime(a.activated_at) : ''}</span>;
        if (!a.sent_method || a.sent_method === 'generated') return <span className="text-gray-400 text-xs">Not sent</span>;
        return <Badge color="blue">{a.sent_method === 'email' ? 'Email' : a.sent_method === 'sms' ? 'SMS' : a.sent_method}</Badge>;
      }
    },
    {
      key: 'device_token',
      header: 'Device Token',
      render: (a: any) => {
        if (!a.device_token) return <span className="text-gray-400">—</span>;
        return (
          <span className="text-xs font-mono text-gray-600">
            {a.device_token.substring(0, 12)}...{a.device_token.substring(a.device_token.length - 4)}
          </span>
        );
      }
    },
    {
      key: 'send_code',
      header: 'Video App',
      render: (a: any) => {
        const msg = codeMessages[a.id];
        const sending = sendingCode.has(a.id);
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSendCode(a)}
              disabled={sending}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 rounded hover:bg-emerald-100 disabled:opacity-50"
            >
              {sending ? (
                <span className="animate-spin">⏳</span>
              ) : (
                <Send className="w-3 h-3" />
              )}
              {a.tenant_email ? 'Send' : 'Send Code'}
            </button>
            <button
              onClick={() => { setAltEmailModal({ open: true, activation: a }); setAltEmail(''); }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-50 rounded hover:bg-gray-100"
            >
              Other Email
            </button>
            {msg && (
              <span className={`text-xs ${msg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {msg.text}
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'actions',
      header: '',
      render: (a: any) => a.tenant_id ? <button className="row-open" onClick={() => navigate(`/tenants/${a.tenant_id}`)}>Open <span aria-hidden="true">→</span></button> : null,
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div className="shadow-list-page prototype-page mobile-app-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mobile App</h1>
          <p className="text-gray-500 mt-1">Mobile and intercom app activations</p>
        </div>
        <button
          onClick={() => exportToCSV(filtered)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="prototype-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search people, properties, or units..." />
        <label>
          <small>Property</small>
          <select value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)}>
            <option value="all">All Properties</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>
          <small>Status</small>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="invited">Invited</option>
          </select>
        </label>
        <button className="prototype-refresh" onClick={load} aria-label="Refresh mobile activations"><RefreshCw /> <span>Refresh</span></button>
        <Button onClick={() => exportToCSV(filtered)}><Download className="w-4 h-4" /> Export CSV</Button>
      </div>

      <CountBand value={filtered.length} detail="People in the current accessible portfolio" />

      {/* App Type Toggle removed — only Mobile App activations are shown
          here. The Intercom App tab used to filter by app_type=='intercom'
          but kiosk/intercom activations now live on the Intercoms tab
          inside each property. */}

      <div className="legacy-summary-cards grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Activations</p>
              <p className="text-2xl font-bold text-gray-900">{totalActivationRecords}</p>
            </div>
            <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-brand-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Activated</p>
              <p className="text-2xl font-bold text-green-600">{totalActivated}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-amber-600">{totalPending}</p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-2">
          {filtered.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">No mobile users found.</div>
          ) : paginatedData.map((a: any) => {
            const active = a.status === 'active';
            return (
              <div key={a.id || a.uuid} className="bg-white rounded-xl border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{a.tenant_name || `Tenant ${a.tenant_id}`}</p>
                    <p className="text-xs text-gray-500 truncate">{a.property_name || ''}{a.unit_number ? ` · Unit ${a.unit_number}` : ''}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${active ? 'bg-green-100 text-green-700' : a.status === 'invited' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{active ? 'Active' : a.status === 'invited' ? 'Invited' : 'Inactive'}</span>
                </div>
                {a.activation_code && <div className="mt-1"><SensitiveCredential value={a.activation_code}/></div>}
                {a.platform && <p className="text-xs text-gray-500 capitalize">{a.platform === 'ios' ? '🍎 iPhone' : '🤖 Android'}{a.device_model ? ` · ${a.device_model}` : ''}</p>}
                {a.tenant_id && <div className="mobile-card-actions"><button onClick={() => navigate(`/tenants/${a.tenant_id}`)} className="row-open">Open person <span aria-hidden="true">→</span></button></div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop table */}
      <Card padding={false} className="hidden md:block">
        <div className="legacy-table-controls p-4 border-b border-gray-100">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by tenant, property, unit, or code..."
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={propertyFilter}
                onChange={(e) => setPropertyFilter(e.target.value === 'all' ? 'all' : e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
              >
                <option value="all">All Properties</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-2 text-sm rounded-lg ${
                  statusFilter === 'all'
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-3 py-2 text-sm rounded-lg ${
                  statusFilter === 'active'
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setStatusFilter('inactive')}
                className={`px-3 py-2 text-sm rounded-lg ${
                  statusFilter === 'inactive'
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Inactive
              </button>
              <button onClick={() => setStatusFilter('invited')} className={`px-3 py-2 text-sm rounded-lg ${statusFilter === 'invited' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Invited</button>
            </div>
          </div>
        </div>
        <PageNav safePage={safePage} totalPages={totalPages} startIndex={startIndex} endIndex={endIndex} filteredLength={filtered.length} setPage={setPage} />
        <Table
          columns={columns}
          data={paginatedData}
          emptyMessage="No mobile users found"
        />
        <PageNav safePage={safePage} totalPages={totalPages} startIndex={startIndex} endIndex={endIndex} filteredLength={filtered.length} setPage={setPage} />
      </Card>

      {/* Send to Alternate Email Modal */}
      <Modal open={altEmailModal.open} onClose={() => setAltEmailModal({ open: false })} title="Send Activation Code">
        <p className="text-sm text-gray-600 mb-3">
          Send activation code to {altEmailModal.activation?.tenant_name || 'tenant'} at a different email address.
        </p>
        <Input label="Email Address" type="email" value={altEmail} onChange={(e: any) => setAltEmail(e.target.value)} placeholder="Enter email address" />
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setAltEmailModal({ open: false })}>Cancel</Button>
          <Button onClick={async () => {
            if (!altEmail || !altEmailModal.activation) return;
            const a = altEmailModal.activation;
            setSendingCode(prev => new Set(prev).add(a.id));
            try {
              const data = await apiPost('/video/send-activation', { tenantId: a.tenant_id, email: altEmail, tenantName: a.tenant_name || 'Tenant' });
              if (data.success) {
                setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'success', text: `Sent to ${altEmail}` } }));
              } else {
                setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'error', text: data.error || 'Failed' } }));
              }
            } catch {
              setCodeMessages(prev => ({ ...prev, [a.id]: { type: 'error', text: 'Network error' } }));
            } finally {
              setSendingCode(prev => { const n = new Set(prev); n.delete(a.id); return n; });
              setAltEmailModal({ open: false });
            }
          }}>Send</Button>
        </div>
      </Modal>
    </div>
  );
};
